import { sortByLastName } from '../../../../../modules/sortingMethods';
import { pluralize } from '../../../../Shared/index';

export const formatDate = (date: Date | string | number): string => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

// reads only the calendar date: already in the teacher's time zone, and Safari can't parse "2026-11-07 15:15:00"
export function formatShortDate(date?: string | null): string | null {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) { return null }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export const ALL = 'ALL'

export const DIAGNOSTIC_CLASSIFICATION_ID = 4

// hardcoded ids are the codebase convention — update alongside getIconForActivityClassification and modules/activity_classifications.js
export const CLASSIFICATIONS: Record<number, { name: string, key: string }> = {
  1: { name: 'Proofreader', key: 'proofreader' },
  2: { name: 'Grammar', key: 'grammar' },
  4: { name: 'Diagnostic', key: 'diagnostic' },
  5: { name: 'Connect', key: 'connect' },
  6: { name: 'Lessons', key: 'lessons' },
  9: { name: 'Reading for Evidence', key: 'evidence' },
}

export interface PackActivity {
  activityId: string;
  name: string;
  classificationId: number;
  uaId?: number;
  cuId?: string;
  classroomId?: string;
  dueDate?: string;
  publishDate?: string;
  scheduled?: boolean;
  staggered?: boolean;
  createdAt?: string;
}

export interface PackClassroom {
  id: string;
  name: string;
  assignOnJoin?: boolean;
  assignedEpoch?: number;
  assignedStudentCount: number;
  totalStudentCount: number;
  staggered?: boolean;
}

export interface Pack {
  unitId: string;
  unitName: string;
  unitCreatedEpoch: number;
  open?: boolean;
  ownedByCurrentUser?: boolean;
  ownerName?: string;
  classrooms: PackClassroom[];
  activities: PackActivity[];
}

export interface StudentCompletion {
  id: number;
  name: string | null;
  completed: boolean;
  started?: boolean;
  progress_pct?: number;
  avg_score?: number | null;
}

export interface ClassroomProgress {
  assigned_count: number;
  completed_any_count: number;
  completed_all_count: number;
  total_completed_activities: number;
  avg_score_pct: number | null;
  last_active?: string | null;
  parent_diagnostic_activity_id?: number | null;
  boosted_at?: string | null;
  classroom_unit_id?: number;
  students?: StudentCompletion[];
}

// older assign flows reordered words and padded whitespace, so compare word sets after the exact check
export function isDefaultPackName(unitName?: string, defaultName?: string | null): boolean {
  if (!unitName || !defaultName) { return false }
  if (unitName === defaultName) { return true }
  const tokens = (value: string) => (value.toLowerCase().match(/[a-z0-9]+/g) || []).sort().join(' ')
  return tokens(unitName) === tokens(defaultName)
}

export interface UnitProgress {
  unit_template_id: number | null;
  from_recommendations: boolean;
  template_name?: string | null;
  parent_diagnostic_activity_ids?: number[];
  contains_diagnostic: boolean;
  activities_count: number;
  last_active: string | null;
  classrooms: Record<string, ClassroomProgress>;
}

export interface RecommendationInfo {
  assigned: number;
  completed_practice: number;
}

export type RecommendationCounts = Record<string, Record<string, RecommendationInfo>>

export interface AggregatedProgress {
  assigned: number;
  completedAny: number;
  completedAll: number;
  avgActivitiesPct: number;
  avgActivitiesCompleted: number;
  activitiesCount: number;
  avgScorePct: number | null;
  lastActive: string | null;
}

export function lastActiveFor(unitProgress: UnitProgress | undefined, selectedClassroomId: string | number | null): string | null {
  if (!unitProgress) { return null }
  if (selectedClassroomId !== ALL && selectedClassroomId !== null) {
    const classroomProgress = unitProgress.classrooms[String(selectedClassroomId)]
    if (classroomProgress && classroomProgress.last_active !== undefined) { return classroomProgress.last_active }
  }
  return unitProgress.last_active || null
}

export const isPackInClassroomScope = (pack: Pack, selectedClassroomId: string | number): boolean =>
  selectedClassroomId === ALL || pack.classrooms.some(c => String(c.id) === String(selectedClassroomId))

export function isProperDiagnosticUnit(pack: Pack): boolean {
  return pack.activities.length === 1 && Number(pack.activities[0].classificationId) === DIAGNOSTIC_CLASSIFICATION_ID
}

export function aggregateProgress(pack: Pack, unitProgress: UnitProgress | undefined, selectedClassroomId: string | number): AggregatedProgress {
  const classroomEntries = unitProgress ? Object.entries(unitProgress.classrooms) : []
  const relevantEntries = selectedClassroomId === ALL
    ? classroomEntries
    : classroomEntries.filter(([classroomId]) => classroomId === String(selectedClassroomId))

  let assigned = 0
  let completedAny = 0
  let completedAll = 0
  let totalCompleted = 0
  let scoreSum = 0
  let scoreWeight = 0

  relevantEntries.forEach(([classroomId, classroomProgress]) => {
    const packClassroom = pack.classrooms.find(c => String(c.id) === classroomId)
    assigned += classroomProgress.assigned_count ?? (Number(packClassroom?.assignedStudentCount) || 0)
    completedAny += classroomProgress.completed_any_count
    completedAll += classroomProgress.completed_all_count
    totalCompleted += classroomProgress.total_completed_activities
    if (classroomProgress.avg_score_pct !== null && classroomProgress.completed_any_count > 0) {
      scoreSum += classroomProgress.avg_score_pct * classroomProgress.completed_any_count
      scoreWeight += classroomProgress.completed_any_count
    }
  })

  const activitiesCount = unitProgress?.activities_count || pack.activities.length

  return {
    assigned,
    completedAny,
    completedAll,
    avgActivitiesPct: assigned && activitiesCount ? Math.min(100, Math.round((totalCompleted / (assigned * activitiesCount)) * 100)) : 0,
    avgActivitiesCompleted: assigned ? Math.min(activitiesCount, Math.round(totalCompleted / assigned)) : 0,
    activitiesCount,
    avgScorePct: scoreWeight ? Math.round(scoreSum / scoreWeight) : null,
    lastActive: lastActiveFor(unitProgress, selectedClassroomId),
  }
}

export function studentCompletionsFor(unitProgress: UnitProgress | undefined, selectedClassroomId: string | number): StudentCompletion[] {
  if (!unitProgress) { return [] }
  const relevantEntries = Object.entries(unitProgress.classrooms)
    .filter(([classroomId]) => selectedClassroomId === ALL || classroomId === String(selectedClassroomId))

  const byId = new Map<number, StudentCompletion>()
  relevantEntries.forEach(([, classroomProgress]) => {
    (classroomProgress.students || []).forEach(student => {
      const existing = byId.get(student.id)
      if (!existing || (student.completed && !existing.completed)) { byId.set(student.id, student) }
    })
  })
  return sortByStatusThenLastName(Array.from(byId.values()))
}

function statusRank(student: StudentCompletion): number {
  if (student.completed) { return 0 }
  return student.started ? 1 : 2
}

export function sortByStatusThenLastName(students: StudentCompletion[]): StudentCompletion[] {
  return [...students].sort((a, b) => (statusRank(a) - statusRank(b)) || sortByLastName(a.name || '', b.name || ''))
}

export function recommendationInfoFor(recommendationCounts: RecommendationCounts, activityId: string | number, classroomIds: Array<string | number>): RecommendationInfo {
  const byClassroom = recommendationCounts[String(activityId)] || {}
  return classroomIds.reduce((totals, classroomId) => {
    const info = byClassroom[String(classroomId)]
    if (info) {
      totals.assigned += info.assigned
      totals.completed_practice += info.completed_practice
    }
    return totals
  }, { assigned: 0, completed_practice: 0 })
}

function diagnosticReportLink(activityId: string | number, classroomId: string | number, segment: string, unitId?: string | number): string {
  const base = `/teachers/progress_reports/diagnostic_reports/diagnostics/${activityId}/classroom/${classroomId}/${segment}`
  return unitId ? `${base}?unit=${unitId}` : base
}

export function diagnosticSummaryLink(activityId: string | number, classroomId: string | number, unitId?: string | number, isPost?: boolean): string {
  return diagnosticReportLink(activityId, classroomId, isPost ? 'growth_summary' : 'summary', unitId)
}

export function diagnosticRecommendationsLink(activityId: string | number, classroomId: string | number, unitId?: string | number): string {
  return diagnosticReportLink(activityId, classroomId, 'recommendations', unitId)
}

export function diagnosticStudentResponsesLink(activityId: string | number, classroomId: string | number, studentId: number): string {
  return diagnosticReportLink(activityId, classroomId, `responses/${studentId}`)
}

export const nameMatchesSearch = (name: string | null | undefined, searchQuery: string) => Boolean(searchQuery && name && name.toLowerCase().includes(searchQuery.toLowerCase()))

export const studentOverviewLink = (classroomId: string | number, studentId: number, unitId: string | number) => `/teachers/progress_reports/student_overview?classroom_id=${classroomId}&student_id=${studentId}#unit-${unitId}`

export function diagnosticResultsLink(activityId: string | number, classroomId: string | number, isPost?: boolean): string {
  return diagnosticReportLink(activityId, classroomId, isPost ? 'growth_results' : 'results')
}

export const BOOSTED_SECTION_ID = 'boosted'

export function assignGrowthDiagnosticLink(activityId: string | number, classroomId: string | number, unitId?: string | number): string {
  return diagnosticReportLink(activityId, classroomId, 'assign_growth_diagnostic', unitId)
}

export function completionBucket(anyCompleted: number, allCompleted: number, assigned: number): 'not_started' | 'in_progress' | 'completed' {
  if (anyCompleted === 0) { return 'not_started' }
  if (assigned > 0 && allCompleted >= assigned) { return 'completed' }
  return 'in_progress'
}

// the Shared Tooltip renders tooltipText via innerHTML — escape user-controlled strings
// there and ONLY there (React escapes JSX/aria, double-escaping breaks screen readers)
export function escapeHtml(value: string): string {
  const replacements: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
  return value.replace(/[&<>"']/g, char => replacements[char])
}

// the assigned count caps at the roster: raw assignments can include departed
// students, and "21 of 19" must never show
export const cappedAssignedCount = (assigned: number, roster: number) => (roster ? Math.min(assigned, roster) : assigned)

function classStudentCounts(classroom: PackClassroom): { shown: number, roster: number } {
  const roster = Number(classroom.totalStudentCount || 0)
  return { shown: cappedAssignedCount(Number(classroom.assignedStudentCount || 0), roster), roster }
}

export function classAssignmentLine(classroom: PackClassroom): string {
  const { shown, roster } = classStudentCounts(classroom)
  const counts = roster
    ? `${shown} of ${roster} ${pluralize(roster, 'student', 'students')}`
    : `${shown} ${pluralize(shown, 'student', 'students')}`
  return `${counts} in ${classroom.name}`
}

export function assignedStudentsSentence({ shown, roster, where, isDiagnostic }: { shown: number, roster: number, where: string, isDiagnostic?: boolean }): string {
  const what = isDiagnostic ? 'this diagnostic' : 'this pack'
  if (!roster) { return `${shown} ${pluralize(shown, 'student', 'students')} in ${where} ${pluralize(shown, 'is', 'are')} assigned ${what}.` }
  if (shown < roster) { return `${shown} of ${roster} students in ${where} ${pluralize(shown, 'is', 'are')} assigned ${what}.` }
  if (roster === 1) { return `The only student in ${where} is assigned ${what}.` }
  return `All ${roster} students in ${where} are assigned ${what}.`
}

export function completedStudentsSentence(completed: number, assigned: number): string {
  const verb = completed === 1 || assigned === 1 ? 'has' : 'have'
  return `${completed} of ${assigned} ${verb} completed it.`
}

export function classAssignmentSentence(classroom: PackClassroom, isDiagnostic?: boolean): string {
  return assignedStudentsSentence({ ...classStudentCounts(classroom), where: classroom.name, isDiagnostic })
}

export function toLegacyCaUnit(pack: Pack) {
  const classroomActivities = new Map()
  pack.activities.forEach(activity => {
    classroomActivities.set(activity.activityId, {
      activityClassificationId: String(activity.classificationId),
      activityId: activity.activityId,
      created_at: activity.createdAt,
      cuId: activity.cuId,
      classroomId: activity.classroomId,
      dueDate: activity.dueDate,
      name: activity.name,
      ownedByCurrentUser: pack.ownedByCurrentUser,
      ownerName: pack.ownerName,
      publishDate: activity.publishDate,
      scheduled: activity.scheduled,
      staggered: activity.staggered,
      uaId: activity.uaId,
    })
  })
  return {
    classroomActivities,
    classrooms: pack.classrooms.map(c => ({ assignedStudentCount: c.assignedStudentCount, name: c.name, totalStudentCount: c.totalStudentCount })),
    open: pack.open,
    unitId: pack.unitId,
    unitName: pack.unitName,
  }
}

function returnHereParam(): string {
  return `redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`
}

export function rosterCountFor(pack: Pack, selectedClassroomId: string | number): number {
  return pack.classrooms
    .filter(classroom => selectedClassroomId === ALL || String(classroom.id) === String(selectedClassroomId))
    .reduce((sum, classroom) => sum + Number(classroom.totalStudentCount || 0), 0)
}

export const selectedClassroomFor = (classrooms: PackClassroom[], selectedClassroomId: string | number | null) =>
  classrooms.find(classroom => String(classroom.id) === String(selectedClassroomId))

export function packAssignedEpochFor(pack: Pack, selectedClassroomId: string | number | null): number {
  const classroom = selectedClassroomFor(pack.classrooms, selectedClassroomId)
  return classroom?.assignedEpoch || pack.unitCreatedEpoch
}

export const packCardId = (unitId: string | number) => `pack-${unitId}`

export const diagnosticCardId = (classroomId: string | number, activityId: string | number) => `diagnostic-${classroomId}-${activityId}`

export const canManagePack = (pack: Pack) => pack.open !== false && pack.ownedByCurrentUser !== false

export function manageStudentsLink(unitId: string | number): string {
  return `/teachers/classrooms/activity_planner/units/${unitId}/students/edit?${returnHereParam()}`
}

export function manageActivitiesLink(unitId: string | number, unitName: string): string {
  return `/teachers/classrooms/activity_planner/units/${unitId}/activities/edit/${encodeURIComponent(unitName)}?${returnHereParam()}`
}

export const WELCOME_SNACKBAR_STORAGE_KEY = 'manageActivitiesV2ShowWelcomeSnackbar'
